import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { AlertOctagon, Wrench, Route, UserCheck, ArrowUpRight } from "lucide-react";

/**
 * Overview widget — the 4 report numbers admins/clients care about, on a
 * rolling window chosen by the parent (via `cadence` prop) so the whole
 * dashboard page can share a single 7D/30D/90D control.
 *
 * `cadence` is passed straight through as the `range` query param
 * (`7d` / `30d` / `90d`). When mounted without a cadence prop we fall
 * back to `7d` so the component still works if reused on a page without
 * a shared control.
 */
const CARDS = [
  { key: "incidents",            label: "Incidents",             caption: "All opened in window",     icon: AlertOctagon, tone: "#FF3B30", to: "/incidents",           testid: "glance-incidents" },
  { key: "maintenance_requests", label: "Maintenance requests",  caption: "All opened in window",     icon: Wrench,       tone: "#FF9F0A", to: "/maintenance",         testid: "glance-maintenance" },
  { key: "tour_scans",           label: "Tour scans",            caption: "GPS + NFC checkpoints",    icon: Route,        tone: "#0A84FF", to: "/tour-scans",          testid: "glance-tours" },
  { key: "supervisor_visits",    label: "Supervisor visits",     caption: "All in window",            icon: UserCheck,    tone: "#22C55E", to: "/supervisor-visits",   testid: "glance-supervisor-visits" },
];

const RANGE_LABEL = { "7d": "Last 7 days", "30d": "Last 30 days", "90d": "Last 90 days" };

export default function ReportsAtAGlance({ cadence = "7d" }) {
  const range = RANGE_LABEL[cadence] ? cadence : "7d";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    api.get("/reports-glance", { params: { range } })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({}); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [range]);

  useEffect(() => load(), [load]);

  return (
    <section
      data-testid="reports-glance"
      className="border border-[#27272A] bg-[#121212]"
    >
      <div className="px-5 py-4 border-b border-[#27272A] flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-mute">Client Reports · {RANGE_LABEL[range]}</div>
          <div className="font-display uppercase font-bold text-xl mt-1">At a glance</div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">
          {loading ? "Loading…" : "Live"}
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-[#27272A]">
        {CARDS.map(({ key, label, caption, icon: Icon, tone, to, testid }) => {
          const v = data?.[key];
          return (
            <Link
              key={key}
              to={to}
              data-testid={testid}
              className="group p-5 hover:bg-[#1c1c1e] transition-colors relative"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="label-mute">{label}</div>
                  <div
                    className="font-display font-black text-4xl mt-2"
                    style={{ color: (v ?? 0) > 0 ? tone : "#3F3F46" }}
                  >
                    {loading ? "—" : (v ?? 0)}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-1 truncate">{caption}</div>
                </div>
                <div
                  className="w-8 h-8 flex items-center justify-center border border-[#27272A] group-hover:border-zinc-500 transition-colors"
                  style={{ color: tone }}
                >
                  <Icon className="size-4" />
                </div>
              </div>
              <ArrowUpRight className="absolute bottom-3 right-3 size-3 text-zinc-600 group-hover:text-zinc-300 transition-colors" />
            </Link>
          );
        })}
      </div>
    </section>
  );
}
